import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";

export const API_BASE_URL = process.env.API_BASE_URL || "https://api.crays.co.za/app";
const API_TIMEOUT = process.env.API_TIMEOUT
  ? parseInt(process.env.API_TIMEOUT, 10)
  : 10000;

declare module "axios" {
  interface AxiosRequestConfig {
    // Lets callers that show their own upgrade UI opt out of the global /subscribe redirect.
    skipSubscriptionRedirect?: boolean;
  }
}

// Append /v1 + route to API_BASE_URL (which already includes /app)
const VERSION_PREFIX = "/v1";

// Auth tokens live in httpOnly cookies (set by the backend) rather than
// localStorage/JS-readable state — withCredentials sends them automatically
// on every same-site request, and no Authorization header is ever attached.
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Prepend version prefix to all request URLs
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (config.url && !config.url.startsWith(VERSION_PREFIX)) {
    config.url =
      VERSION_PREFIX +
      (config.url.startsWith("/") ? config.url : "/" + config.url);
  }
  return config;
});

let refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  try {
    await axios.post(
      `${apiClient.defaults.baseURL}${VERSION_PREFIX}/auth/refresh`,
      null,
      { withCredentials: true },
    );
    return true;
  } catch {
    return false;
  } finally {
    refreshPromise = null;
  }
}

export function refreshSession(): Promise<boolean> {
  if (!refreshPromise) refreshPromise = doRefresh();
  return refreshPromise;
}

// Auth endpoints whose 401 means bad credentials or no session, not an expired access token.
const NO_REFRESH_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/verify-otp",
  "/auth/resend-otp",
  "/auth/refresh",
  "/auth/logout",
  "/auth/forgot-password",
  "/auth/reset-password",
];

let onSessionExpired: (() => void) | null = null;

// Registered by the store so this module never has to import it (store -> api -> apiClient).
export function setSessionExpiredHandler(handler: () => void) {
  onSessionExpired = handler;
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;
    if (!originalRequest) return Promise.reject(error);

    if (
      error.response?.status === 403 &&
      error.response?.data?.data?.subscriptionRequired &&
      !originalRequest.skipSubscriptionRedirect
    ) {
      window.location.href = "/subscribe";
      return Promise.reject(error);
    }

    const requestPath: string = originalRequest.url ?? "";
    const isNoRefreshPath = NO_REFRESH_PATHS.some((path) => requestPath.endsWith(path));

    if (error.response?.status === 401 && !originalRequest._retry && !isNoRefreshPath) {
      originalRequest._retry = true;

      const refreshed = await refreshSession();
      if (refreshed) {
        return apiClient(originalRequest);
      }

      onSessionExpired?.();
    }

    return Promise.reject(error);
  },
);

export default apiClient;
